import { useEffect, useRef, useState } from "react";

type Props = {
  images: { src: string; alt?: string }[];
  intervalMs?: number;   // Auto-Play Intervall
  className?: string;    // Wrapper-Klassen (z.B. Höhenvorgabe)
  rounded?: string;      // Tailwind rounded-Klasse, default: rounded-2xl
};

export default function ImageCarousel({
  images,
  intervalMs = 4000,
  className = "",
  rounded = "rounded-2xl",
}: Props) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const go = (n: number) => setIdx((p) => (p + n + images.length) % images.length);
  const goTo = (n: number) => setIdx(((n % images.length) + images.length) % images.length);

  useEffect(() => {
    if (paused || images.length <= 1) return;
    const t = setInterval(() => go(1), intervalMs);
    return () => clearInterval(t);
  }, [paused, images.length, intervalMs]);

  return (
    <div
      className={`relative overflow-hidden ${rounded} ${className} bg-white border border-slate-200 shadow-xl`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => (touchStartX.current = e.touches[0].clientX)}
      onTouchEnd={(e) => {
        const start = touchStartX.current;
        if (start == null) return;
        const delta = e.changedTouches[0].clientX - start;
        if (Math.abs(delta) > 40) go(delta > 0 ? -1 : 1);
        touchStartX.current = null;
      }}
      aria-roledescription="carousel"
    >
      {/* Slides */}
      <div
        className="whitespace-nowrap transition-transform duration-500"
        style={{ transform: `translateX(-${idx * 100}%)` }}
      >
        {images.map((img, i) => (
          <div key={i} className="inline-block w-full align-top">
            <img
              src={img.src}
              alt={img.alt ?? `Slide ${i + 1}`}
              className="block w-full h-full object-cover object-top"
            />
          </div>
        ))}
      </div>

      {/* Prev / Next */}
      {images.length > 1 && (
        <>
          <button
            aria-label="Vorheriges Bild"
            onClick={() => go(-1)}
            className="absolute left-2 top-1/2 -translate-y-1/2 px-3 py-2 rounded-xl bg-black/40 hover:bg-black/55 backdrop-blur text-white"
          >
            ‹
          </button>
          <button
            aria-label="Nächstes Bild"
            onClick={() => go(1)}
            className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-2 rounded-xl bg-black/40 hover:bg-black/55 backdrop-blur text-white"
          >
            ›
          </button>
        </>
      )}

      {/* Dots */}
      {images.length > 1 && (
        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
          {images.map((_, i) => (
            <button
              key={i}
              aria-label={`Zu Bild ${i + 1}`}
              onClick={() => goTo(i)}
              className={`h-2.5 w-2.5 rounded-full transition ${
                i === idx ? "bg-black/80" : "bg-black/30 hover:bg-black/50"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
